// utils/StorageManager.js
// Centralized localStorage management for game settings

const STORAGE_KEY = "game_settings";
const HNS_ROUND_KEY = "hold_and_win_round";
// FREE_ROUNDS_COMPLETION_KEY removed - now using unified FREE_ROUND_KEY for both active and completion
// BET_SETTINGS_KEY and ROUND_BET_KEY removed - bet is stored in base_game_round
const BASE_GAME_ROUND_KEY = "base_game_round";
const FREE_ROUND_KEY = "free_round";
const SESSION_KEY = "session_token";

/**
 * StorageManager - Handles all localStorage operations for game settings
 * Provides a clean, extensible API for persisting user preferences
 */
class StorageManager {
  // ============================================
  // SESSION MANAGEMENT METHODS
  // ============================================

  /**
   * Get current session token from window.__OPTIONS__
   * Uses cache_player_id as the session identifier
   * @returns {string|null} Session token or null if not available
   */
  static getCurrentSessionToken() {
    return window.__OPTIONS__?.cache_player_id || null;
  }

  /**
   * Get stored session token from localStorage
   * @returns {string|null} Stored session token or null if not found
   */
  static getStoredSessionToken() {
    try {
      return localStorage.getItem(SESSION_KEY);
    } catch (error) {
      return null;
    }
  }

  /**
   * Save current session token to localStorage
   * @param {string} token - Session token to save
   */
  static saveSessionToken(token) {
    try {
      if (token) {
        localStorage.setItem(SESSION_KEY, token);
      }
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if current session matches stored session
   * @returns {boolean} True if session matches, false if new session or no stored session
   */
  static isSessionValid() {
    const currentToken = this.getCurrentSessionToken();
    const storedToken = this.getStoredSessionToken();

    // If no current token, can't validate
    if (!currentToken) {
      return false;
    }

    // If no stored token, treat as new session
    if (!storedToken) {
      return false;
    }

    // Compare tokens
    return currentToken === storedToken;
  }

  /**
   * Clear all session-specific data from localStorage
   * Called when session token mismatch is detected
   * Clears everything for a fresh start with new player
   */
  static clearSessionData() {
    try {
      // Clear session-specific data
      localStorage.removeItem(BASE_GAME_ROUND_KEY);
      localStorage.removeItem(FREE_ROUND_KEY);
      localStorage.removeItem(HNS_ROUND_KEY);
      // FREE_ROUNDS_COMPLETION_KEY removed - using unified FREE_ROUND_KEY

      // Clear user preferences (sound and UI settings) for fresh start
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  // ============================================
  // SOUND SETTINGS METHODS
  // ============================================

  /**
   * Load sound settings from localStorage
   * @returns {Object} Sound settings object with defaults if not found
   */
  static loadSoundSettings() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return this._getDefaultSoundSettings();
      }

      const parsed = JSON.parse(data);

      // Validate and return sound settings with defaults for missing values
      const soundSettings = parsed.sound || {};
      return {
        masterVolume: this._validateNumber(
          soundSettings.masterVolume,
          1.0,
          0,
          2,
        ),
        soundEffectsEnabled: this._validateBoolean(
          soundSettings.soundEffectsEnabled,
          true,
        ),
        backgroundMusicEnabled: this._validateBoolean(
          soundSettings.backgroundMusicEnabled,
          true,
        ),
        globalSoundDisabled: this._validateBoolean(
          soundSettings.globalSoundDisabled,
          false,
        ),
      };
    } catch (error) {
      return this._getDefaultSoundSettings();
    }
  }

  /**
   * Save sound settings to localStorage
   * @param {Object} settings - Sound settings object
   * @param {number} settings.masterVolume - Master volume (0-2)
   * @param {boolean} settings.soundEffectsEnabled - Sound effects enabled
   * @param {boolean} settings.backgroundMusicEnabled - Background music enabled
   * @param {boolean} settings.globalSoundDisabled - Global sound disabled
   */
  static saveSoundSettings(settings) {
    try {
      // Load existing data to preserve other settings categories
      const existingData = this._loadRawData();

      // Update sound settings
      existingData.sound = {
        masterVolume: settings.masterVolume,
        soundEffectsEnabled: settings.soundEffectsEnabled,
        backgroundMusicEnabled: settings.backgroundMusicEnabled,
        globalSoundDisabled: settings.globalSoundDisabled,
      };

      // Save back to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load UI settings from localStorage
   * @returns {Object} UI settings object with defaults if not found
   */
  static loadUISettings() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return this._getDefaultUISettings();
      }

      const parsed = JSON.parse(data);
      const uiSettings = parsed.ui || {};

      // Resolve autoPlaySpeed: prefer stored string, fall back to turboSpinEnabled boolean
      let autoPlaySpeed = "auto";
      if (typeof uiSettings.autoPlaySpeed === "string" &&
          ["auto", "quick", "turbo"].includes(uiSettings.autoPlaySpeed)) {
        autoPlaySpeed = uiSettings.autoPlaySpeed;
      } else if (uiSettings.turboSpinEnabled === true) {
        autoPlaySpeed = "turbo";
      }

      return {
        spacebarSpinEnabled: this._validateBoolean(
          uiSettings.spacebarSpinEnabled,
          true,
        ),
        turboSpinEnabled: this._validateBoolean(
          uiSettings.turboSpinEnabled,
          false,
        ),
        turboSuggestionDeclined: this._validateBoolean(
          uiSettings.turboSuggestionDeclined,
          false,
        ),
        autoPlaySpeed,
      };
    } catch (error) {
      return this._getDefaultUISettings();
    }
  }

  /**
   * Save UI settings to localStorage
   * @param {Object} settings - UI settings object
   * @param {boolean} settings.spacebarSpinEnabled - Spacebar spin enabled
   * @param {boolean} settings.turboSpinEnabled - Turbo spin enabled (autoPlaySpeed === "turbo")
   */
  static saveUISettings(settings) {
    try {
      const existingData = this._loadRawData();

      existingData.ui = {
        spacebarSpinEnabled: settings.spacebarSpinEnabled,
        turboSpinEnabled: settings.turboSpinEnabled,
        turboSuggestionDeclined: settings.turboSuggestionDeclined,
        autoPlaySpeed: settings.autoPlaySpeed || "auto",
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Clear all stored settings (useful for debugging)
   */
  static clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Get all stored data (for debugging)
   * @returns {Object} All stored settings
   */
  static getAll() {
    try {
      return this._loadRawData();
    } catch (error) {
      return {};
    }
  }

  // ============================================
  // HOLD AND WIN ROUND METHODS
  // ============================================

  /**
   * Save Hold and Win round state (active or completion)
   * Called during each respin in Hold & Win feature
   * Stores parallel to base game round (doesn't override it)
   *
   * When respinsLeft === 0, also includes completion data (totalWin, isMaxWin)
   *
   * @param {Object} roundData - Round data to save
   * @param {Object} roundData.round - Current round with reels, wins, win, storage
   * @param {number} roundData.respinsLeft - Remaining respins (0 for completion)
   * @param {number} roundData.progressBar - Progress bar value (0-20)
   * @param {Array|null} roundData.coinMultipliers - Coin multipliers array
   * @param {number} roundData.bet - Bet amount
   * @param {number} roundData.balance - Current balance
   * @param {boolean} [roundData.introShown] - Whether intro has been shown (undefined = preserve existing)
   * @param {number} [roundData.totalWin] - Total win (only for completion, respinsLeft === 0)
   * @param {boolean} [roundData.isMaxWin] - Max win flag (only for completion, respinsLeft === 0)
   */
  static saveHoldAndWinRound(roundData) {
    try {
      // Load existing data to preserve introShown flag if not explicitly provided
      const existing = this.loadHoldAndWinRound();

      const data = {
        timestamp: Date.now(),
        round: roundData.round,
        respinsLeft: roundData.respinsLeft,
        progressBar: roundData.progressBar || 0,
        coinMultipliers: roundData.coinMultipliers || null,
        bet: roundData.bet,
        balance: roundData.balance,
        introShown:
          roundData.introShown !== undefined
            ? roundData.introShown
            : (existing?.introShown ?? false),
      };

      // Add completion-specific fields when respinsLeft === 0
      if (roundData.respinsLeft === 0) {
        data.totalWin = roundData.totalWin;
        data.isMaxWin = roundData.isMaxWin;
      }

      localStorage.setItem(HNS_ROUND_KEY, JSON.stringify(data));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load Hold and Win active round state
   * Called during game initialization to detect mid-feature reload
   *
   * @returns {Object|null} Active round data or null if not found
   */
  static loadHoldAndWinRound() {
    try {
      const data = localStorage.getItem(HNS_ROUND_KEY);
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);

      // Validate required fields
      if (
        !parsed.round ||
        parsed.respinsLeft === undefined ||
        parsed.bet === undefined
      ) {
        this.clearHoldAndWinRound();
        return null;
      }

      return parsed;
    } catch (error) {
      // Clear corrupted data
      this.clearHoldAndWinRound();
      return null;
    }
  }

  /**
   * Clear Hold and Win active round state
   * Called on first base game spin after Hold & Win completes
   */
  static clearHoldAndWinRound() {
    try {
      localStorage.removeItem(HNS_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if Hold and Win active round data exists
   *
   * @returns {boolean} True if active round data exists
   */
  static hasHoldAndWinRound() {
    try {
      const data = localStorage.getItem(HNS_ROUND_KEY);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  // ============================================
  // FREE ROUNDS COMPLETION METHODS - REMOVED
  // ============================================
  // Free Rounds now use a unified FREE_ROUND_KEY for both active and completion phases
  // Similar to Hold & Win consolidation, completion is detected by performed === count
  // All completion methods removed - use saveFreeRound/loadFreeRound instead

  // ============================================
  // BASE GAME ROUND STORAGE METHODS
  // ============================================

  /**
   * Save base game round state
   *
   * IMPORTANT: This key serves as the "return point" for Hold & Win:
   * - During base game: Stores current base game round
   * - During Free Rounds: ALSO stores current free round (for H&W return point)
   * - During Hold & Win: Contains the trigger round (base OR free) to return to
   *
   * Called after every successful base game spin AND every free round spin
   * Replaces previous saved round (only one round stored at a time)
   *
   * @param {Object} roundData - Round data to save
   * @param {Object} roundData.round - Round data (reels, wins, win amount)
   * @param {number} roundData.bet - Bet value used for this round
   * @param {number} roundData.balance - Balance after this round
   */
  static saveBaseGameRound(roundData) {
    try {
      const data = {
        timestamp: Date.now(),
        round: roundData.round,
        bet: roundData.bet,
        balance: roundData.balance,
      };

      localStorage.setItem(BASE_GAME_ROUND_KEY, JSON.stringify(data));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load base game round state
   * Called during game initialization
   *
   * @returns {Object|null} Round data or null if not found
   */
  static loadBaseGameRound() {
    try {
      const data = localStorage.getItem(BASE_GAME_ROUND_KEY);
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);

      // Validate required fields to prevent loading corrupted data
      if (!parsed.round || parsed.bet === undefined) {
        this.clearBaseGameRound();
        return null;
      }

      return parsed;
    } catch (error) {
      // Clear corrupted data
      this.clearBaseGameRound();
      return null;
    }
  }

  /**
   * Clear base game round state
   * Called when free rounds end (user was in free rounds, not base game)
   */
  static clearBaseGameRound() {
    try {
      localStorage.removeItem(BASE_GAME_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if base game round data exists
   *
   * @returns {boolean} True if round data exists
   */
  static hasBaseGameRound() {
    try {
      const data = localStorage.getItem(BASE_GAME_ROUND_KEY);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  // ============================================
  // FREE ROUND STORAGE METHODS
  // ============================================

  /**
   * Save free round state
   * Called after every successful free round spin
   * Replaces previous saved round (only one round stored at a time)
   *
   * @param {Object} roundData - Round data to save
   * @param {Object} roundData.round - Round data (reels, wins, win amount)
   * @param {number} roundData.bet - Bet value used for this round
   * @param {number} roundData.balance - Balance after this round (frozen during free rounds)
   * @param {number} roundData.performed - Number of free rounds performed so far
   * @param {number} roundData.count - Total number of free rounds granted
   * @param {number} roundData.totalWin - Accumulated win across all free rounds so far
   */
  /**
   * Save free round state (unified for both active and completion phases)
   *
   * IMPORTANT: This method now handles both active and completion phases:
   * - Active phase: performed < count (mid-feature)
   * - Completion phase: performed === count (feature complete, awaiting outro)
   *
   * The phase is determined by comparing performed vs count when loading.
   * betToRestore: Base game bet to restore when free rounds end (set on first save)
   *
   * @param {Object} roundData - Round data to save
   * @param {Object} roundData.round - Round outcome
   * @param {number} roundData.bet - Bet amount (free round bet)
   * @param {number} roundData.balance - Current balance (frozen during free rounds)
   * @param {number} roundData.performed - Number of free rounds performed
   * @param {number} roundData.count - Total number of free rounds
   * @param {number} roundData.totalWin - Accumulated total win
   * @param {number} [roundData.betToRestore] - Base game bet to restore when free rounds end
   */
  static saveFreeRound(roundData) {
    try {
      let betToRestore = roundData.betToRestore;
      if (betToRestore === undefined && roundData.performed === 0) {
        const baseGame = this.loadBaseGameRound();
        if (baseGame?.bet !== undefined) {
          betToRestore = baseGame.bet;
        }
      }
      const existing = this.loadFreeRound();
      if (betToRestore === undefined && existing?.betToRestore !== undefined) {
        betToRestore = existing.betToRestore;
      }

      const data = {
        timestamp: Date.now(),
        round: roundData.round,
        bet: roundData.bet,
        balance: roundData.balance,
        performed: roundData.performed,
        count: roundData.count,
        totalWin: roundData.totalWin || 0,
      };
      if (betToRestore !== undefined) {
        data.betToRestore = betToRestore;
      }

      localStorage.setItem(FREE_ROUND_KEY, JSON.stringify(data));
    } catch (error) {
      // Don't throw - localStorage failures shouldn't break the game
    }
  }

  /**
   * Load free round state (unified for both active and completion phases)
   * Called during game initialization
   *
   * Phase detection:
   * - If performed < count: Active phase (mid-feature)
   * - If performed === count: Completion phase (feature complete, awaiting outro)
   *
   * @returns {Object|null} Round data or null if not found
   */
  static loadFreeRound() {
    try {
      const data = localStorage.getItem(FREE_ROUND_KEY);
      if (!data) {
        return null;
      }

      const parsed = JSON.parse(data);

      // Validate required fields to prevent loading corrupted data
      if (!parsed.round || parsed.bet === undefined) {
        this.clearFreeRound();
        return null;
      }

      return parsed;
    } catch (error) {
      // Clear corrupted data
      this.clearFreeRound();
      return null;
    }
  }

  /**
   * Clear free round state
   * Called when free rounds end (transition back to base game)
   */
  static clearFreeRound() {
    try {
      localStorage.removeItem(FREE_ROUND_KEY);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Check if free round data exists
   *
   * @returns {boolean} True if round data exists
   */
  static hasFreeRound() {
    try {
      const data = localStorage.getItem(FREE_ROUND_KEY);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear prize/winners table data from localStorage
   * Removes legacy data from unused feature
   */
  static clearPrizeWinnersData() {
    try {
      // Check common possible keys for prize/winners data
      const possibleKeys = [
        "prize",
        "winners",
        "prizeData",
        "winnersTable",
        "prizeWinners",
        "showIntroScreen",
        "currentPageIndex",
        "pages",
      ];

      // Also check all localStorage keys for data matching the structure
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        try {
          const data = localStorage.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            // Check if this data matches the prize/winners structure
            if (
              parsed.showIntroScreen !== undefined ||
              parsed.currentPageIndex !== undefined ||
              parsed.pages !== undefined ||
              parsed.prizeData !== undefined ||
              parsed.prizeDataSource !== undefined
            ) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          // Skip non-JSON data
          continue;
        }
      }

      // Also remove known possible keys
      possibleKeys.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore errors
        }
      });
    } catch (error) {
      // Silent fail - don't break game if cleanup fails
    }
  }

  // ============================================
  // PRIVATE HELPER METHODS
  // ============================================

  /**
   * Load raw data from localStorage
   * @private
   */
  static _loadRawData() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      return {};
    }
  }

  /**
   * Get default sound settings
   * @private
   */
  static _getDefaultSoundSettings() {
    return {
      masterVolume: 1.0,
      soundEffectsEnabled: true,
      backgroundMusicEnabled: true,
      globalSoundDisabled: false,
    };
  }

  /**
   * Get default UI settings
   * @private
   */
  static _getDefaultUISettings() {
    return {
      spacebarSpinEnabled: true,
      turboSpinEnabled: false,
      turboSuggestionDeclined: false,
      autoPlaySpeed: "auto",
    };
  }

  /**
   * Validate a number value with min/max bounds
   * @private
   */
  static _validateNumber(value, defaultValue, min, max) {
    if (typeof value !== "number" || isNaN(value)) {
      return defaultValue;
    }
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Validate a boolean value
   * @private
   */
  static _validateBoolean(value, defaultValue) {
    return typeof value === "boolean" ? value : defaultValue;
  }
}

export default StorageManager;
